<template lang="pug" id="call">
.message-wrapper(:style="{textAlign: textAlign}")
  .message-webchat(v-if="inWebchat")
    .message-avatar(v-if="senderType === 'agents'",)
      img(v-if="avatar"
         :src="avatar" 
         :alt="avatarAltText")
      .no-image(v-else="senderInitials") {{ senderInitials }}
    template(v-if="inWebchat")
      .message-bubble-webchat-container(
        :class="{'message-customer': senderType === 'customers',\
          'message-agent': senderType === 'agents' }")
        .message-bubble_header
            .message-bubble_date
              aiq-timeago(:since="messageDate" :maxTime="maxTime")
        .call-state-group
          aiq-col.icon-group(:span="5")
            component(:is="icon").call-icon
          aiq-col.message-group(:span="24")
            div(v-if="!isCallEnded") {{ inviteMessage }}
            div(v-else)
              span {{ endedMessage }}
              p.duration(v-show="callDuration" :aria-hidden="true") {{ callDuration }}
              span.sr-only(v-show="callDuration") {{ noAbbreviationCallDuration }}
            aiq-button.join-btn(v-if="isVideoOrAudioCall"
                                size="small"
                                @click="onJoin"
                                :disabled="isCallEnded || callJoined") {{ buttonLabel }}
  .message-container(v-else
                    :class="{'message-customer': senderType === 'customers',\
                            'message-agent': senderType === 'agents' }")
    .message-item(v-if="senderType")
      .message-avatar
        img(v-if="avatar"
           :src="avatar" 
           :alt="avatarAltText")
        .no-image(v-else-if="!avatar && senderInitials") {{ senderInitials }}
        img(v-else 
           :src="photo"
           :alt="avatarAltText")
      .message-bubble
        .message-bubble_header
          .message-bubble_name
            .message-bubble_user-name(v-if="senderType === 'agents'")
              | {{ senderName }}
            i(v-if="senderType === 'customers'",
                :title="channelInfo.title",
                :class="`aiq-ico-conversation-${channel}`")
            .message-bubble_user-name(v-if="senderType === 'customers'")
              | {{ customerName }}
          .message-bubble_date
            aiq-timeago(:since="messageDate" :maxTime="maxTime")
        .call-state-group
          aiq-col.icon-group(:span="5")
            component(:is="icon").call-icon
          aiq-col.message-group(:span="24")
            div(v-if="!isCallEnded") {{ inviteMessage }}
            div(v-else)
              span {{ endedMessage }}
              p.duration(v-show="callDuration") {{ callDuration }}
            aiq-button.join-btn(v-if="isVideoOrAudioCall"
                                size="small"
                                @click="onJoin"
                                :disabled="isCallEnded || callJoined") {{ buttonLabel }}

</template>

<script>
import { get, isEmpty, upperFirst } from 'lodash';
import BaseMessage from './BaseMessage/BaseMessage.vue';
import Avatar from '../../Avatar.vue';
import VideoIcon from '../../../assets/video/video-grey.vue';
import CobrowseIcon from '../../../assets/common/cobrowse-gray.vue';
import { timeDelta } from '../../../lib/formatters';

const CALL_TYPES = {
  video: 'Video call',
  cobrowse: 'Cobrowse session',
};

export default {
  extends: BaseMessage,
  components: {
    Avatar,
    VideoIcon,
    CobrowseIcon,
  },
  name: 'callMessage',
  props: {
    inWebchat: Boolean,
    callJoined: Boolean,
  },
  computed: {
    callTypes() {
      return CALL_TYPES;
    },
    classNames() {
      const message = {
        'message-customer': this.senderType === 'customers',
        'message-agent': this.senderType === 'agents',
      };

      return {
        attachments: {
          'message-attachment-customer': this.senderType === 'customers',
          'message-attachment-agent': this.senderType === 'agents',
        },
        message,
        messageWithLocation: {
          'message-customer': message['message-customer'],
          'message-agent': message['message-agent'],
          'message-webchat': this.inWebchat,
        },
      };
    },
    icon() {
      return `${upperFirst(this.callType)}Icon`;
    },
    isCallEnded() {
      return !isEmpty(get(this.message, 'payload.call.ended_at', ''));
    },
    isVideoOrAudioCall() {
      return ['video', 'audio'].includes(this.callType);
    },
    inviteMessage() {
      return this.getString(`messages.${this.callType}.invite`, `${this.callTypes[this.callType]} invitation`);
    },
    endedMessage() {
      return this.getString(`messages.${this.callType}.ended`, `${this.callTypes[this.callType]} ended`);
    },
    buttonLabel() {
      return this.getString(`messages.${this.callType}.button`, `Join the ${this.callTypes[this.callType].toLowerCase()}`);
    },
    callDuration() {
      const duration = timeDelta(get(this.message, 'payload.call.duration', '0'));
      return this.getString(`messages.${this.callType}.duration`, { duration, defaultValue: `Lasted ${duration}` });
    },
    noAbbreviationCallDuration() {
      const duration = timeDelta(get(this.message, 'payload.call.duration', '0'), false);
      return this.getString(`messages.${this.callType}.duration`, { duration, defaultValue: `Lasted ${duration}.` });
    },
    callEndedPhrase() {
      return 'Call Ended';
    },
    callType() {
      return get(this.message, 'payload.call.type', '');
    },
  },
  methods: {
    onJoin() {
      return this.$emit('call', this.message);
    },
  },
};
</script>

<style lang="scss" scoped>
.action-group {
  display: flex;
  justify-content: center;
  align-items: center;
}

.call-state-group {
  display: flex;
  background-color: white;
  border-radius: 4px;
  margin-top: 3px;
  padding: 5px 30px 5px 10px;
  min-width: 200px;
}

.icon-group {
  padding-top: 10px;
  margin-right: 5px;
}

.call-icon {
  vertical-align: middle;
}

.message-group {
  padding: 10px 0px;
  color: #67737f;
  flex: auto;

  .duration {
    font-size: 12px;
    color: rgba(103, 115, 127, 1);
  }

  .join-btn {
    margin-top: 5px;
  }
}

.sr-only {
  clip: rect(1px, 1px, 1px, 1px);
  clip-path: inset(50%);
  height: 1px;
  overflow: hidden;
  position: absolute;
  white-space: nowrap;
  width: 1px;
  margin: -1px;
}
</style>
